import React, { useCallback, useEffect, useRef } from 'react';
import {
  Dialog,
  Button,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  CircularProgress,
  Tooltip,
  Divider,
} from '@mui/material';
import CloseIconButton from '../../../../components/common/CloseIconButton';
import { outlinedGrayButtonSx } from '../../../../utils/typographyStyles';
import type { LinkedRecipient as Recipient } from './newInvoiceForm.helper';

export type { Recipient };

// ---------------------------------------------------------------------------
// Style tokens — centralised for easy theming
// ---------------------------------------------------------------------------

const tokens = {
  radius: {
    dialog: '20px',
    button: '10px',
    row: '10px',
    icon: '50%',
  },
  color: {
    text: '#1a1a1a',
    textMuted: '#444',
    textHint: '#888',
    highlight: '#555',
    btnFill: '#424242',
    btnFillHover: '#333',
    btnDisabledBg: '#e0e0e0',
    btnDisabledFg: '#9e9e9e',
    rowHover: 'rgba(0, 0, 0, 0.04)',
    divider: 'rgba(0, 0, 0, 0.08)',
    iconBg: '#f5f5f5',
  },
  spacing: {
    dialogWidth: 440,
    listMaxHeight: 320,
    rowMinHeight: 52,
  },
} as const;

const dialogPaperSx = {
  borderRadius: tokens.radius.dialog,
  width: tokens.spacing.dialogWidth,
  maxWidth: '95vw',
  p: 0,
  overflow: 'hidden',
  boxShadow: '0px 8px 40px rgba(0, 0, 0, 0.12)',
} as const;

const proceedBtnSx = {
  borderRadius: tokens.radius.button,
  py: 1.3,
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.9375rem',
  backgroundColor: tokens.color.btnFill,
  boxShadow: 'none',
  '&:hover': { backgroundColor: tokens.color.btnFillHover, boxShadow: 'none' },
  '&.Mui-disabled': { backgroundColor: tokens.color.btnDisabledBg, color: tokens.color.btnDisabledFg },
} as const;

const cancelBtnSx = {
  ...outlinedGrayButtonSx,
  borderRadius: tokens.radius.button,
  py: 1.3,
  textTransform: 'none',
  fontSize: '0.9375rem',
} as const;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface RecipientSelectionModalProps {
  /** Controls modal visibility. */
  open: boolean;
  /** Title shown in the header. */
  title?: string;
  /** Called on X / Cancel / overlay click / Esc. */
  onClose: () => void;
  /** Called with the selected recipient id, or `undefined` when no recipients exist. */
  onProceed: (recipientId?: number) => void;
  /** The list of recipients to display. */
  recipients: Recipient[];
  /** Currently selected recipient id (controlled). */
  selectedRecipientId: number | null;
  /** Callback when a recipient row is clicked or navigated via keyboard. */
  onSelectRecipient: (id: number) => void;
  /** Show a centred spinner instead of the list. */
  loading?: boolean;
  /** When true (default), Proceed is disabled until a recipient is selected (ignored when list is empty). */
  proceedDisabledUntilSelected?: boolean;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Centred spinner while recipients are loading. */
const LoadingState: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }} role="status">
    <CircularProgress size={28} aria-label="Loading recipients" />
  </Box>
);

/** Friendly empty state with icon + guidance text. */
const EmptyState: React.FC = () => (
  <Box sx={{ py: 3.5, px: 2, textAlign: 'center' }}>
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: tokens.radius.icon,
        backgroundColor: tokens.color.iconBg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mx: 'auto',
        mb: 2,
        fontSize: '1.4rem',
      }}
      aria-hidden="true"
    >
      👥
    </Box>
    <Typography variant="body2" sx={{ fontWeight: 500, color: tokens.color.textMuted, lineHeight: 1.6 }}>
      No recipients linked to this donor
    </Typography>
    <Typography variant="caption" sx={{ display: 'block', color: tokens.color.textHint, lineHeight: 1.5, mt: 0.75 }}>
      The invoice will be saved as&nbsp;
      <Box component="span" sx={{ fontWeight: 600, color: tokens.color.highlight }}>unsettled</Box>
      &nbsp;and can be assigned to a recipient later.
    </Typography>
  </Box>
);

/** Single clickable recipient row with radio button. */
const RecipientRow: React.FC<{
  recipient: Recipient;
  isSelected: boolean;
  onClick: (id: number) => void;
}> = ({ recipient, isSelected, onClick }) => {
  const label = `${recipient.name ?? ''}${recipient.mrn ? ` | ${recipient.mrn}` : ''}`;

  return (
    <Tooltip title={label} enterDelay={800} placement="top-start">
      <Box
        onClick={() => onClick(recipient.id)}
        role="option"
        aria-selected={isSelected}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick(recipient.id);
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          minHeight: tokens.spacing.rowMinHeight,
          px: 1.5,
          borderRadius: tokens.radius.row,
          cursor: 'pointer',
          transition: 'background-color 0.15s',
          backgroundColor: isSelected ? tokens.color.rowHover : 'transparent',
          '&:hover': { backgroundColor: tokens.color.rowHover },
          '&:focus-visible': {
            outline: `2px solid ${tokens.color.btnFill}`,
            outlineOffset: -2,
          },
        }}
      >
        <FormControlLabel
          value={recipient.id}
          control={<Radio size="small" tabIndex={-1} />}
          label={
            <Typography
              variant="body2"
              sx={{
                fontWeight: isSelected ? 600 : 400,
                fontSize: '0.9375rem',
                color: tokens.color.text,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 320,
              }}
            >
              {label}
            </Typography>
          }
          sx={{
            m: 0,
            width: '100%',
            pointerEvents: 'none', // click handled by parent Box
            '& .MuiFormControlLabel-label': { flex: 1, minWidth: 0 },
          }}
        />
      </Box>
    </Tooltip>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const RecipientSelectionModal: React.FC<RecipientSelectionModalProps> = ({
  open,
  onClose,
  onProceed,
  title = 'Recipients',
  recipients,
  selectedRecipientId,
  onSelectRecipient,
  loading = false,
  proceedDisabledUntilSelected = true,
}) => {
  const hasRecipients = recipients.length > 0;
  const proceedRef = useRef<HTMLButtonElement>(null);

  // Determine whether Proceed should be enabled
  const canProceed = !loading && (
    !hasRecipients || !proceedDisabledUntilSelected || !!selectedRecipientId
  );

  const handleProceed = useCallback(() => {
    if (!hasRecipients) {
      onProceed();
    } else if (selectedRecipientId) {
      onProceed(selectedRecipientId);
    }
  }, [hasRecipients, selectedRecipientId, onProceed]);

  // Auto-focus Proceed when opening with no recipients (primary action)
  useEffect(() => {
    if (open && !loading && !hasRecipients) {
      // Small delay to ensure dialog animation completes
      const timer = setTimeout(() => proceedRef.current?.focus(), 150);
      return () => clearTimeout(timer);
    }
  }, [open, loading, hasRecipients]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ sx: dialogPaperSx }}
      aria-labelledby="recipient-selection-title"
      aria-modal="true"
    >
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, pt: 3, pb: 2 }}>
        <Typography
          variant="h6"
          component="h2"
          id="recipient-selection-title"
          sx={{ fontWeight: 700, fontSize: '1.2rem', color: tokens.color.text }}
        >
          {title}
        </Typography>
        <CloseIconButton onClick={onClose} aria-label="Close" />
      </Box>

      {/* ── Content — fixed header/footer, scrollable list ── */}
      <Box sx={{ px: 2.5, pb: 1, maxHeight: tokens.spacing.listMaxHeight, overflowY: 'auto', minHeight: 64 }}>
        {loading ? (
          <LoadingState />
        ) : !hasRecipients ? (
          <EmptyState />
        ) : (
          <RadioGroup
            value={selectedRecipientId ?? ''}
            onChange={(e) => onSelectRecipient(Number(e.target.value))}
          >
            {recipients.map((r, idx) => (
              <React.Fragment key={r.id}>
                <RecipientRow
                  recipient={r}
                  isSelected={selectedRecipientId === r.id}
                  onClick={onSelectRecipient}
                />
                {idx < recipients.length - 1 && (
                  <Divider sx={{ mx: 1.5, borderColor: tokens.color.divider }} />
                )}
              </React.Fragment>
            ))}
          </RadioGroup>
        )}
      </Box>

      {/* ── Footer ── */}
      <Box sx={{ display: 'flex', gap: 2, px: 3, pt: 1.5, pb: 3 }}>
        <Button variant="outlined" onClick={onClose} fullWidth sx={cancelBtnSx}>
          Cancel
        </Button>
        <Button
          ref={proceedRef}
          variant="contained"
          disabled={!canProceed}
          fullWidth
          onClick={handleProceed}
          sx={proceedBtnSx}
        >
          {hasRecipients ? 'Assign & Proceed' : 'Save as Unsettled'}
        </Button>
      </Box>
    </Dialog>
  );
};

export default RecipientSelectionModal;
